import { array, boolean, object, string } from "yup";

export const payrollSchema = object().shape({
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].reduce(
    (acc, id) => ({
      ...acc,
      [`notApplicable-${id}`]: boolean().optional(),
      [`paymentDay-${id}`]: string().when(`notApplicable-${id}`, {
        is: (val) => val !== true,
        then: string()
          .required("Dia de pagamento é obrigatório")
          .test(
            "is-valid-day",
            "Informe um dia de 1 a 31",
            value => {
              if (!value) return false;
              const num = Number(value);
              return Number.isInteger(num) && num >= 1 && num <= 31;
            }
          ),
        otherwise: string().optional().nullable(),
      }),
    }),
    {}
  ),
  payrollIsValidated: boolean().optional(),

  anticipatePaymentsOnWeekendsHolidays: string()
    .required("Antecipação de pagamentos é obrigatória")
    .oneOf(["1", "2"], "Selecione uma opção válida para antecipação"),
  anticipatePaymentsDescription: string().when(
    "anticipatePaymentsOnWeekendsHolidays",
    {
      is: "1",
      then: string().required("Descreva a regra de negócio para antecipação"),
      otherwise: string().optional().nullable(),
    },
  ),

  hasMonthlyAdvance: string()
    .required("Adiantamento mensal é obrigatório")
    .oneOf(["1", "2"], "Selecione uma opção válida para adiantamento"),
  advancePercentage: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Informe o percentual do adiantamento")
      .test(
        "between-1-100",
        "O percentual deve ser entre 1 e 100",
        value => {
          const n = Number(value);
          return Number.isInteger(n) && n >= 1 && n <= 100;
        },
      ),
    otherwise: string().optional().nullable(),
  }),
  advanceCalcBase: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Base de cálculo do adiantamento é obrigatória")
      .oneOf(["1", "2"], "Selecione uma opção válida para a base de cálculo"),
    otherwise: string().optional().nullable(),
  }),
  advanceCalcBaseForCommissioned: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Base de cálculo para comissionados é obrigatória")
      .oneOf(["1", "2", "3"], "Selecione uma opção válida para base de cálculo de comissionados"),
    otherwise: string().optional().nullable(),
  }),
  advanceCalcBaseForCommissionedOther: string().when(
    "advanceCalcBaseForCommissioned",
    {
      is: "3",
      then: string().required("Descreva a regra de negócio do adiantamento para comissionados"),
      otherwise: string().optional().nullable(),
    },
  ),
  minDaysWorkedForAdvance: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Informe os dias mínimos trabalhados")
      .test(
        "between-1-30",
        "Os dias mínimos devem ser entre 1 e 30",
        value => {
          const n = Number(value);
          return Number.isInteger(n) && n >= 1 && n <= 30;
        },
      ),
    otherwise: string().optional().nullable(),
  }),
  advancePaymentCalculationType: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Tipo de pagamento do adiantamento é obrigatório")
      .oneOf(["1", "2"], "Selecione um tipo de pagamento válido"),
    otherwise: string().optional().nullable(),
  }),
  newHiresReceiveAdvance: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Adiantamento para recém-contratados é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para recém-contratados"),
    otherwise: string().optional().nullable(),
  }),
  advanceAdhesionNextMonth: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Adesão no mês seguinte é obrigatória")
      .oneOf(["1", "2"], "Selecione uma opção válida para adesão"),
    otherwise: string().optional().nullable(),
  }),
  maternityLeaveReceiveAdvance: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Adiantamento para licença maternidade é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para licença maternidade"),
    otherwise: string().optional().nullable(),
  }),
  proLaboreReceiveAdvance: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Adiantamento para pró-labore é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para pró-labore"),
    otherwise: string().optional().nullable(),
  }),
  internReceiveAdvance: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Adiantamento para estagiário é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para estagiário"),
    otherwise: string().optional().nullable(),
  }),
  apprenticeReceiveAdvance: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Adiantamento para aprendiz é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para aprendiz"),
    otherwise: string().optional().nullable(),
  }),
  irrfAdvanceRetentionRule: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Regra de retenção de IRRF do adiantamento é obrigatória")
      .oneOf(["1", "2", "3"], "Selecione uma regra válida para IRRF"),
    otherwise: string().optional().nullable(),
  }),
  irrfAdvanceRetentionRuleOther: string().when("irrfAdvanceRetentionRule", {
    is: "3",
    then: string().required("Descreva detalhadamente a regra de retenção de IRRF"),
    otherwise: string().optional().nullable(),
  }),
  advanceAnalyticalReportOrder: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Ordem do relatório analítico de adiantamento é obrigatória")
      .oneOf(["1", "2", "3", "4"], "Selecione uma ordem válida para o relatório analítico"),
    otherwise: string().optional().nullable(),
  }),
  advanceNetReport: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Opção de relatório líquido consolidado é obrigatória")
      .oneOf(["1", "2"], "Selecione uma opção válida para o relatório líquido"),
    otherwise: string().optional().nullable(),
  }),
  advanceNetReportExcel: string().when("hasMonthlyAdvance", {
    is: "1",
    then: string()
      .required("Formato Excel do relatório líquido é obrigatório")
      .oneOf(["1", "2", "3"], "Selecione uma opção válida para relatório em Excel"),
    otherwise: string().optional().nullable(),
  }),

  thirteenthFirstInstallmentWriteOff: string()
    .required("Baixa da 1ª parcela é obrigatória")
    .oneOf(["1", "2"], "Selecione uma opção válida para a baixa da 1ª parcela"),
  vacationWriteOffMethod: string()
    .required("Método de baixa de férias é obrigatório")
    .oneOf(["1", "2"], "Selecione um método válido para férias"),
  resignationProvisionWhenTerminated: string()
    .required("Provisão no mês da demissão é obrigatória")
    .oneOf(["1", "2"], "Selecione uma opção válida para provisão na demissão"),
  provisionAnalyticalReportOrder: string()
    .required("Ordem do relatório analítico de provisão é obrigatória")
    .oneOf(["1", "2", "3", "4"], "Selecione uma ordem válida para o relatório de provisão"),
  provisionSyntheticReportOrder: string()
    .required("Ordem do relatório sintético de provisão é obrigatória")
    .oneOf(["1", "2", "3", "4"], "Selecione uma ordem válida para relatório sintético"),
  provisionAccountingSyntheticReportOrder: string()
    .required("Ordem do relatório contábil de provisão é obrigatória")
    .oneOf(["1", "2", "3", "4"], "Selecione uma ordem válida para relatório contábil"),

  thirteenthAdvanceOnBirthday: string()
    .required("Adiantamento do 13º no aniversário é obrigatório")
    .oneOf(["1", "2"], "Selecione uma opção válida para 13º no aniversário"),
  thirteenthFirstInstallmentWithAverages: string()
    .required("Cálculo de médias na 1ª parcela do 13º é obrigatório")
    .oneOf(["1", "2", "3", "4", "5"], "Selecione uma opção válida para cálculo de médias"),
  thirteenthFirstInstallmentWithAveragesOther: string().when(
    "thirteenthFirstInstallmentWithAverages",
    {
      is: "5",
      then: string().required("Descreva a regra de negócio para médias no 13º"),
      otherwise: string().optional().nullable(),
    },
  ),
  internProLaboreThirteenth: string()
    .required("Pagamento de 13º para Pró-Labore/Estagiário é obrigatório")
    .oneOf(["1", "2"], "Selecione uma opção válida para pagamento de 13º para pró-labore/estagiário"),
  internProLaboreThirteenthRubricsNovDec: string()
    .required("Configuração de rubricas do 13º é obrigatória")
    .oneOf(["1", "3", "4"], "Selecione uma opção válida para rubricas do 13º"),
  internProLaboreThirteenthRubricsDecReceive: string().when(
    "internProLaboreThirteenthRubricsNovDec",
    {
      is: "1",
      then: string().required("Informe a(s) rubrica(s) para o 13º recebido em Dezembro"),
      otherwise: string().optional().nullable(),
    },
  ),
  internProLaboreThirteenthRubricsDecOnly: string().when(
    "internProLaboreThirteenthRubricsNovDec",
    {
      is: "3",
      then: string().required("Informe a(s) rubrica(s) exclusivas de Dezembro"),
      otherwise: string().optional().nullable(),
    },
  ),
  internProLaboreThirteenthOtherRule: string().when(
    "internProLaboreThirteenthRubricsNovDec",
    {
      is: "4",
      then: string().required("Descreva a regra de negócio para rubricas de 13º"),
      otherwise: string().optional().nullable(),
    },
  ),
  thirteenthAnalyticalReportOrder: string()
    .required("Ordem do relatório analítico de 13º é obrigatória")
    .oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório analítico de 13º"),
  thirteenthSyntheticReportOrder: string()
    .required("Ordem do relatório sintético de 13º é obrigatória")
    .oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório sintético de 13º"),
  thirteenthNetReport: string()
    .required("Relatório líquido de 13º consolidado é obrigatório")
    .oneOf(["1", "2"], "Selecione uma opção válida para relatório líquido de 13º consolidado"),
  thirteenthNetReportExcel: string()
    .required("Relatório líquido de 13º em Excel é obrigatório")
    .oneOf(["1", "2", "3"], "Selecione uma opção válida para relatório líquido de 13º em excel"),
  
  hasPlr: string()
    .required("Participação de Lucros (PLR) é obrigatório")
    .oneOf(["1", "2"], "Selecione uma opção válida para PLR"),
  plrAccordingToCctAct: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Acordo CCT para PLR é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para acordo PLR"),
    otherwise: string().optional().nullable(),
  }),
  plrBusinessRule: string().when("plrAccordingToCctAct", {
    is: "2",
    then: string().required("Descreva a regra de negócio para PLR"),
    otherwise: string().optional().nullable(),
  }),
  plrFrequencyAndCompetencies: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Frequência e competências de PLR é obrigatória")
      .oneOf(["1", "2", "3", "4"], "Selecione uma frequência válida para PLR"),
    otherwise: string().optional().nullable(),
  }),
  plrSameCalendarYear: array().when("hasPlr", {
    is: "1",
    then: array()
      .of(string())
      .min(1, "Selecione ao menos uma competência para PLR")
      .required("Competências do PLR são obrigatórias"),
    otherwise: array().optional().nullable(),
  }),
  plrAnalyticalReportOrder: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Ordem do relatório analítico de PLR é obrigatória")
      .oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório analítico de plr"),
    otherwise: string().optional().nullable(),
  }),
  plrSyntheticReportOrder: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Ordem do relatório sintético de PLR é obrigatória")
      .oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório sintético de plr"),
    otherwise: string().optional().nullable(),
  }),
  plrNetReport: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Relatório líquido consolidado de PLR é obrigatório")
      .oneOf(["1", "2"], "Selecione uma opção válida para relatório líquido consolidado de plr"),
    otherwise: string().optional().nullable(),
  }),
  plrNetReportExcel: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Geração de arquivo de integração de PLR é obrigatório")
      .oneOf(["1", "2", "3"], "Selecione uma opção válida para integração PLR"),
    otherwise: string().optional().nullable(),
  }),
  plrAccountingSyntheticReportOrder: string().when("hasPlr", {
    is: "1",
    then: string()
      .required("Ordem do relatório contábil de PLR é obrigatório")
      .oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para o relatório contábil PLR"),
    otherwise: string().optional().nullable(),
  }),

  hasConsignedLoan: string()
    .required("Desconto de empréstimo consignado é obrigatório")
    .oneOf(["1", "2"], "Selecione uma opção válida para empréstimo consignado"),
  consignedLoanDeductionLaw: string().when("hasConsignedLoan", {
    is: "1",
    then: string()
      .required("Desconto conforme limite da lei é obrigatório")
      .oneOf(["1", "2", "3"], "Selecione uma regra de limite de lei válida"),
    otherwise: string().optional().nullable(),
  }),
  consignedLoanDeductionOther: string().when("consignedLoanDeductionLaw", {
    is: "3",
    then: string().required("Descreva a regra de negócio do desconto do consignado"),
    otherwise: string().optional().nullable(),
  }),
  consignedLoanFullDeductionIfNoBalance: string().when("hasConsignedLoan", {
    is: "1",
    then: string()
      .required("Desconto integral em caso de sem saldo é obrigatório")
      .oneOf(["1", "2", "3"], "Selecione uma opção válida para desconto sem saldo"),
    otherwise: string().optional().nullable(),
  }),
  consignedLoanFullDeductionIfNoBalanceOther: string().when(
    "consignedLoanFullDeductionIfNoBalance",
    {
      is: "3",
      then: string().required("Descreva a regra de negócio para desconto total do empréstimo sem saldo"),
      otherwise: string().optional().nullable(),
    },
  ),
  consignedLoanPeriod: string().when("hasConsignedLoan", {
    is: "1",
    then: string().required("Período de apuração do consignado é obrigatório"),
    otherwise: string().optional().nullable(),
  }),
  consignedLoanFormat: string().when("hasConsignedLoan", {
    is: "1",
    then: string()
      .required("Formato do arquivo de consignado é obrigatório")
      .oneOf(["1", "2", "3", "4"], "Selecione um formato de arquivo válido"),
    otherwise: string().optional().nullable(),
  }),
  consignedLoanDataReceptionDate: string().when("hasConsignedLoan", {
    is: "1",
    then: string().required("Data de recebimento das informações do consignado é obrigatória"),
    otherwise: string().optional().nullable(),
  }),
});
